import { defineStore } from "pinia"
import { postApiJson, fetchApiCatch } from "@/services/repowerAPIService.js"
import { setPlausibleUserSegment } from "@/services/plausibleService.js"
import HTTP_STATUS from "@/helpers/httpStatus"
import useSessionStore from "@/stores/sessionStore.js"
import useNotificationStore from "@/stores/notificationStore.js"
import useMeteringPointsStore from "@/stores/repower/meteringPointsStore.js"
import useFilterStore from "@/stores/filterStore.js"
import useDashboardStore from "@/stores/dashboardStore.js"
import useNavigationStore from "@/stores/navigationStore.js"

export default defineStore("userStore", {
  state: () => ({
    id: null,
    fullName: "",
    email: "",
    flags: [],
    analyticsSegment: null,
    notificationsSeenUntil: null,
  }),
  actions: {
    setUser(
      id,
      fullName,
      email,
      flags,
      analyticsSegment,
      notificationsSeenUntil,
    ) {
      this.id = id ?? null
      this.fullName = fullName ?? ""
      this.email = email ?? ""
      this.flags = flags ?? []
      this.analyticsSegment = analyticsSegment ?? null
      this.notificationsSeenUntil = notificationsSeenUntil ?? null
    },
    login(email, password, onSuccess, onUnknown, onFail, onLoadFail) {
      const sessionStore = useSessionStore()
      postApiJson("/users/login/", { email, password })
        .then((data) => {
          // Set user and CSRF token
          this.setUser(
            data.user_id,
            data.user_full_name,
            data.user_email,
            data.user_flags,
            data.analytics_segment,
            data.notifications_seen_until,
          )
          sessionStore.setAuthenticated(true)
          sessionStore.requestCsrfToken()
          setPlausibleUserSegment(data.analytics_segment)
          // Prepare other data
          useMeteringPointsStore().loadMeteringPoints(onLoadFail)
          useNavigationStore().applyQuery()
          // Run callback
          onSuccess?.()
        })
        .catch((error) => {
          if (error.code === HTTP_STATUS.BAD_REQUEST) {
            onUnknown?.(error)
          } else {
            onFail?.(error)
          }
        })
    },
    logout(onSuccess, onFail) {
      const meteringPointsStore = useMeteringPointsStore()
      fetchApiCatch("/users/logout/")
        .then(() => {
          // Reset session
          const sessionStore = useSessionStore()
          sessionStore.setAuthenticated(false)
          sessionStore.requestCsrfToken()
          // Reset other stores
          useNotificationStore().$reset()
          meteringPointsStore.$reset()
          useFilterStore().$reset()
          useDashboardStore().$reset()
          this.$reset()
          // Reset custom properties
          setPlausibleUserSegment("unauthenticated")
          // Run callback
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeFullName(fullName, onSuccess, onFail) {
      postApiJson(
        "/users/change-full-name/",
        { full_name: fullName },
        { method: "PUT" },
      )
        .then(() => {
          this.fullName = fullName
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    requestEmailChange(newEmail, onSuccess, onFail) {
      postApiJson(
        "/users/request-email-change/",
        { new_email: newEmail },
        { method: "POST" },
      )
        .then(() => {
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changeEmail(newEmail, token, password, onSuccess, onFail) {
      postApiJson(
        "/users/change-email/",
        { token, password },
        { method: "PUT" },
      )
        .then(() => {
          this.email = newEmail
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    changePassword(currentPassword, newPassword, onSuccess, onFail) {
      postApiJson(
        "/users/change-password/",
        { current_password: currentPassword, new_password: newPassword },
        // Opt out of the central 429 notice: this flow shows its own throttle
        // message ("Te veel pogingen") in AccountSettingsView.
        { method: "PUT", skipRateLimitNotice: true },
      )
        .then(() => {
          onSuccess?.()
        })
        .catch((error) => {
          onFail?.(error)
        })
    },
    hasFlag(flag) {
      return this.flags.includes(flag)
    },
    hasAnyOfFlags(flags) {
      return this.flags.some((flag) => flags.includes(flag))
    },
  },
  persist: true,
})
